import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PictureComponent } from '../../../types/builder';
import { useBuilderStore } from '../../../store/builderStore';
import { AlignLeft, AlignCenter, AlignRight, Upload, Link, ExternalLink, Image, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

interface PictureSettingsProps {
  component: PictureComponent;
}

export const PictureSettings: React.FC<PictureSettingsProps> = ({ component }) => {
  const { t } = useTranslation('builder');
  const { updateComponent } = useBuilderStore();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleUpdate = (updates: Partial<PictureComponent['settings']>) => {
    updateComponent(component.id, {
      settings: { ...component.settings, ...updates },
    });
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(t('settings.common.imageFormatRestricted'));
      return;
    }

    const maxSize = file.type === 'image/gif' ? 10 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > maxSize) {
      const limit = file.type === 'image/gif'
        ? t('settings.picture.fileLimit10MB')
        : t('settings.picture.fileLimit5MB');
      toast.error(t('settings.common.fileTooLarge', { limit }));
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('image', file);

      const token = localStorage.getItem('token');
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

      const response = await fetch(`${API_URL}/upload/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || t('settings.common.uploadFailed'));
      }

      const data = await response.json();
      handleUpdate({ src: data.url });
      toast.success(t('settings.picture.uploadSuccess'));
    } catch (error: any) {
      console.error('Upload error:', error);
      toast.error(error.message || t('settings.picture.uploadFailedSpecific'));
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.picture.label')}</label>

        {component.settings.src && (
          <div className="relative mb-3 group">
            <img
              src={component.settings.src}
              alt={component.settings.alt || 'Preview'}
              className="w-full aspect-square object-cover rounded-lg border border-gray-700"
            />
            <button
              onClick={() => handleUpdate({ src: '' })}
              className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
              title={t('settings.picture.removeTitle')}
            >
              <Trash2 size={16} />
            </button>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          onChange={handleFileUpload}
          className="hidden"
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg font-medium transition-all disabled:opacity-50"
        >
          {isUploading ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              {t('settings.common.uploading')}
            </>
          ) : (
            <>
              <Upload size={18} />
              {t('settings.picture.uploadCta')}
            </>
          )}
        </button>
        <p className="text-xs text-gray-500 mt-2 text-center">
          {t('settings.common.imageHintFull')}
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.picture.orImageUrl')}</label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Image size={16} className="text-gray-500" />
          </div>
          <input
            type="text"
            value={component.settings.src}
            onChange={(e) => handleUpdate({ src: e.target.value })}
            className="w-full pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder={t('settings.picture.imagePlaceholder')}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.altText')}</label>
        <input
          type="text"
          value={component.settings.alt}
          onChange={(e) => handleUpdate({ alt: e.target.value })}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder={t('settings.common.altPlaceholder')}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.linkOptional')}</label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Link size={16} className="text-gray-500" />
          </div>
          <input
            type="text"
            value={component.settings.link || ''}
            onChange={(e) => handleUpdate({ link: e.target.value })}
            className="w-full pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder={t('settings.common.linkPlaceholder')}
          />
        </div>
      </div>

      {component.settings.link && (
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium flex items-center gap-2">
            <ExternalLink size={16} />
            {t('settings.common.openInNewTab')}
          </label>
          <button
            onClick={() => handleUpdate({ openInNewTab: !component.settings.openInNewTab })}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              component.settings.openInNewTab ? 'bg-blue-600' : 'bg-gray-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                component.settings.openInNewTab ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium mb-2">
          {t('settings.common.borderRadiusPx', { value: component.settings.borderRadius })}
        </label>
        <input
          type="range"
          min="0"
          max="50"
          value={component.settings.borderRadius}
          onChange={(e) => handleUpdate({ borderRadius: parseInt(e.target.value) })}
          className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
        />
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>{t('settings.picture.borderRadiusSquare')}</span>
          <span>{t('settings.picture.borderRadiusRounded')}</span>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.shadow')}</label>
        <select
          value={component.settings.shadow}
          onChange={(e) => handleUpdate({ shadow: e.target.value as any })}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="none">{t('settings.common.shadows.none')}</option>
          <option value="sm">{t('settings.common.shadows.sm')}</option>
          <option value="md">{t('settings.common.shadows.md')}</option>
          <option value="lg">{t('settings.common.shadows.lg')}</option>
          <option value="xl">{t('settings.common.shadows.xl')}</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.animation')}</label>
        <select
          value={component.settings.animation}
          onChange={(e) => handleUpdate({ animation: e.target.value as any })}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="none">{t('settings.common.animations.none')}</option>
          <option value="fadeIn">{t('settings.common.animations.fadeIn')}</option>
          <option value="zoomIn">{t('settings.common.animations.zoomIn')}</option>
          <option value="slideUp">{t('settings.common.animations.slideUp')}</option>
          <option value="bounce">{t('settings.common.animations.bounce')}</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium mb-3">{t('settings.common.alignment')}</label>
        <div className="grid grid-cols-3 gap-2">
          <button
            onClick={() => handleUpdate({ alignment: 'left' })}
            className={`p-3 rounded-lg border ${
              component.settings.alignment === 'left'
                ? 'bg-white text-gray-900 border-white'
                : 'bg-gray-800 border-gray-700 text-white hover:bg-gray-700'
            } transition-colors`}
          >
            <AlignLeft size={18} className="mx-auto" />
          </button>
          <button
            onClick={() => handleUpdate({ alignment: 'center' })}
            className={`p-3 rounded-lg border ${
              component.settings.alignment === 'center'
                ? 'bg-white text-gray-900 border-white'
                : 'bg-gray-800 border-gray-700 text-white hover:bg-gray-700'
            } transition-colors`}
          >
            <AlignCenter size={18} className="mx-auto" />
          </button>
          <button
            onClick={() => handleUpdate({ alignment: 'right' })}
            className={`p-3 rounded-lg border ${
              component.settings.alignment === 'right'
                ? 'bg-white text-gray-900 border-white'
                : 'bg-gray-800 border-gray-700 text-white hover:bg-gray-700'
            } transition-colors`}
          >
            <AlignRight size={18} className="mx-auto" />
          </button>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">
          {t('settings.common.paddingPx', { value: component.settings.padding })}
        </label>
        <input
          type="range"
          min="0"
          max="64"
          value={component.settings.padding}
          onChange={(e) => handleUpdate({ padding: parseInt(e.target.value) })}
          className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
        />
      </div>
    </div>
  );
};















